/* eslint-disable @typescript-eslint/no-explicit-any */
import { firstNonNullable, isObject, keys, makeNonNullableArray } from '@/lib/helpers';
import type { PropSanitiser, SanitisedUnion, SanitiserCallbacks } from './sanitiserTypes';

export const createPropSanitiser = <Raw, C extends SanitiserCallbacks>(
  _rawElement: () => Raw | null,
  callbacks: C
): PropSanitiser<Raw, C> => {
  const matchesTypeName = (element: Record<PropertyKey, any>, typeNames: PropertyKey[]) =>
    !typeNames.length || typeNames.includes(element.__typename as PropertyKey);

  const sanitiseProp = (key: string, value: unknown): unknown => {
    const callback = callbacks[key];

    if (!callback) return value;
    if (callback === 'sanitise') return many(value as MaybeArrayOf<Raw>);
    if (callback === 'sanitiseSingle') return one(value as MaybeArrayOf<Raw>);

    return callback(value);
  };

  function one(
    element?: MaybeArrayOf<Raw>,
    typeNames?: MaybeArrayOf<PropertyKey>
  ): SanitisedUnion<Raw, C> | null {
    const source = firstNonNullable(element);

    if (!isObject(source)) return null;
    if (!matchesTypeName(source, makeNonNullableArray(typeNames))) return null;

    return keys(source).reduce((result, key) => {
      result[key] = sanitiseProp(String(key), source[key]);
      return result;
    }, {} as Record<PropertyKey, unknown>) as SanitisedUnion<Raw, C>;
  }

  function many(
    elements?: MaybeArrayOf<Raw>,
    typeNames?: MaybeArrayOf<PropertyKey>
  ): SanitisedUnion<Raw, C>[] {
    return makeNonNullableArray(elements).reduce((result, element) => {
      const sanitised = one(element as Raw, typeNames);
      if (sanitised) result.push(sanitised);
      return result;
    }, [] as SanitisedUnion<Raw, C>[]);
  }

  return { one, many };
};
